// =============================================================================
// AuthProvider — real Amazon Cognito authentication (SRP)
// =============================================================================
//
// Uses `amazon-cognito-identity-js` against the deployed User Pool. The public
// SPA app client has no secret and supports ALLOW_USER_SRP_AUTH +
// ALLOW_REFRESH_TOKEN_AUTH. The pool's username attribute is EMAIL.
//
// The library persists the session (id/access/refresh tokens) to localStorage
// by default via CognitoUserPool. `getToken()` returns a *valid* ID token,
// transparently refreshing it via the stored refresh token when it is near or
// past expiry. The backend verifies the ID token (issuer + JWKS signature +
// expiry + token_use + aud == client id).
// =============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { createApiClient, type ApiClient } from "../api/client";

// --- Config (env with baked-in defaults for the deployed pool) --------------

const USER_POOL_ID: string =
  import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "us-east-1_H9yEYCVxc";
const CLIENT_ID: string =
  import.meta.env.VITE_COGNITO_CLIENT_ID ?? "75e6al0l230svsqs355vi29amb";

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
});

// Refresh proactively when the ID token has this little life (seconds) left.
const REFRESH_MARGIN_SECONDS = 30;

// --- Types ------------------------------------------------------------------

/** Result of a sign-in attempt. */
export type SignInResult =
  | { status: "success" }
  | { status: "newPasswordRequired" };

interface AuthContextValue {
  isAuthenticated: boolean;
  email: string | null;
  /** Sign in via SRP. Resolves with the outcome (may require a new password). */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Complete the NEW_PASSWORD_REQUIRED challenge for an admin-created user. */
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
  /** Returns a valid ID token, refreshing transparently; null if signed out. */
  getToken: () => Promise<string | null>;
  /** API client bound to `getToken` (attaches the Bearer token per call). */
  api: ApiClient;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// --- Helpers ----------------------------------------------------------------

function emailFromSession(session: CognitoUserSession): string | null {
  const payload = session.getIdToken().decodePayload() as {
    email?: unknown;
    "cognito:username"?: unknown;
  };
  if (typeof payload.email === "string") return payload.email;
  if (typeof payload["cognito:username"] === "string")
    return payload["cognito:username"];
  return null;
}

/** Resolve the current session, letting the SDK auto-refresh if expired. */
function getCurrentSession(): Promise<CognitoUserSession | null> {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
}

// --- Provider ---------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  // Local-preview escape hatch: skip Cognito and present a signed-in session so
  // the UI can be viewed against the local mock API without credentials. Gated
  // on BOTH the dev server (`import.meta.env.DEV`, false in any `vite build`)
  // AND an explicit VITE_PREVIEW=true (set only in dashboard/.env.local), so a
  // production bundle can never bypass auth even if the flag leaks into its env.
  if (import.meta.env.DEV && import.meta.env.VITE_PREVIEW === "true") {
    return <PreviewAuthProvider>{children}</PreviewAuthProvider>;
  }
  return <CognitoAuthProvider>{children}</CognitoAuthProvider>;
}

/** Static, always-authenticated context for local preview (no Cognito). */
function PreviewAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(() => {
    const getToken = async () => null;
    return {
      isAuthenticated: true,
      email: "preview@localhost",
      signIn: async () => ({ status: "success" as const }),
      completeNewPassword: async () => {},
      signOut: () => {},
      getToken,
      api: createApiClient(getToken),
    };
  }, []);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [ready, setReady] = useState(false);

  // Holds the CognitoUser that is mid NEW_PASSWORD_REQUIRED challenge.
  const pendingUserRef = useRef<CognitoUser | null>(null);

  const applySession = useCallback((session: CognitoUserSession) => {
    setEmail(emailFromSession(session));
    setIsAuthenticated(true);
  }, []);

  // Restore any persisted session on mount.
  useEffect(() => {
    let cancelled = false;
    getCurrentSession()
      .then((session) => {
        if (cancelled) return;
        if (session) applySession(session);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const signIn = useCallback(
    (emailInput: string, password: string): Promise<SignInResult> => {
      const username = emailInput.trim();
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });
      cognitoUser.setAuthenticationFlowType("USER_SRP_AUTH");
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      return new Promise<SignInResult>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            pendingUserRef.current = null;
            applySession(session);
            resolve({ status: "success" });
          },
          onFailure: (err) => {
            pendingUserRef.current = null;
            reject(err instanceof Error ? err : new Error(String(err)));
          },
          newPasswordRequired: () => {
            // Admin-created user with a temporary password. Keep the user around
            // so completeNewPassword can finish the challenge on the same object.
            pendingUserRef.current = cognitoUser;
            resolve({ status: "newPasswordRequired" });
          },
        });
      });
    },
    [applySession],
  );

  const completeNewPassword = useCallback(
    (newPassword: string): Promise<void> => {
      const cognitoUser = pendingUserRef.current;
      if (!cognitoUser) {
        return Promise.reject(
          new Error("No pending new-password challenge. Sign in again."),
        );
      }
      return new Promise<void>((resolve, reject) => {
        // Pass no extra attributes: email is already set and immutable here.
        cognitoUser.completeNewPasswordChallenge(
          newPassword,
          {},
          {
            onSuccess: (session) => {
              pendingUserRef.current = null;
              applySession(session);
              resolve();
            },
            onFailure: (err) => {
              reject(err instanceof Error ? err : new Error(String(err)));
            },
          },
        );
      });
    },
    [applySession],
  );

  const signOut = useCallback(() => {
    const user = userPool.getCurrentUser();
    if (user) user.signOut();
    pendingUserRef.current = null;
    setIsAuthenticated(false);
    setEmail(null);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    const user = userPool.getCurrentUser();
    if (!user) return null;

    const session = await getCurrentSession();
    if (!session) {
      // Session invalid and could not be refreshed — treat as signed out.
      setIsAuthenticated(false);
      setEmail(null);
      return null;
    }

    const idToken = session.getIdToken();
    const now = Math.floor(Date.now() / 1000);
    if (idToken.getExpiration() - now > REFRESH_MARGIN_SECONDS) {
      return idToken.getJwtToken();
    }

    // Near/at expiry: refresh explicitly using the stored refresh token.
    const refreshed = await new Promise<CognitoUserSession | null>(
      (resolve) => {
        user.refreshSession(
          session.getRefreshToken(),
          (err: Error | null, next: CognitoUserSession | null) => {
            resolve(err || !next ? null : next);
          },
        );
      },
    );

    if (refreshed) {
      applySession(refreshed);
      return refreshed.getIdToken().getJwtToken();
    }
    // Refresh failed but current token is still technically present — return it.
    return idToken.getJwtToken();
  }, [applySession]);

  // Single API client instance; it calls getToken() lazily on each request.
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      email,
      signIn,
      completeNewPassword,
      signOut,
      getToken,
      api,
    }),
    [
      isAuthenticated,
      email,
      signIn,
      completeNewPassword,
      signOut,
      getToken,
      api,
    ],
  );

  // Avoid a flash of the login screen while restoring a persisted session.
  if (!ready) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
